import { ApolloError } from '@apollo/client';

import {
  useGroupAttendanceSummaryQuery,
  GroupAttendanceSummaryQuery as GroupAttendanceSummaryDoc,
  useGroupPerformanceSummaryQuery,
  GroupPerformanceSummaryQuery as GroupPerformanceSummaryDoc,
  useGroupRevenueAggregatesQuery,
  GroupRevenueAggregatesQuery as GroupRevenueAggregatesDoc,
  useGroupLessonsQuery,
  GroupLessonsQuery as GroupLessonsDoc,
  useAllZoomRecordingsQuery,
  AllZoomRecordingsQuery as AllZoomRecordingsDoc,
  useGroupMaterialsQuery,
  GroupMaterialsQuery as GroupMaterialsDoc,
  MaterialKindEnum,
} from 'core/graphql';

import { useStableRefetch } from './useStableRefetch';

/* ---------- group-scoped attendance summary (TZ §G3) ---------- */

export type IGroupAttendanceSummary = NonNullable<
  GroupAttendanceSummaryDoc['groupAttendanceSummary']
>;

interface IGroupAttendanceSummaryHook {
  data?: IGroupAttendanceSummary;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupAttendanceSummary = (
  groupId?: string,
): IGroupAttendanceSummaryHook => {
  const { data, loading, error, refetch } = useGroupAttendanceSummaryQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    data: data?.groupAttendanceSummary ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ---------- group-scoped performance summary (TZ §G4) ---------- */

export type IGroupPerformanceSummary = NonNullable<
  GroupPerformanceSummaryDoc['groupPerformanceSummary']
>;

interface IGroupPerformanceSummaryHook {
  data?: IGroupPerformanceSummary;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupPerformanceSummary = (
  groupId?: string,
): IGroupPerformanceSummaryHook => {
  const { data, loading, error, refetch } = useGroupPerformanceSummaryQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    data: data?.groupPerformanceSummary ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ---------- group-scoped revenue rollup (TZ §G5) ---------- */

export type IGroupRevenueAggregates = NonNullable<
  GroupRevenueAggregatesDoc['groupRevenueAggregates']
>;

interface IGroupRevenueAggregatesHook {
  data?: IGroupRevenueAggregates;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupRevenueAggregates = (
  groupId?: string,
  skip?: boolean,
): IGroupRevenueAggregatesHook => {
  const { data, loading, error, refetch } = useGroupRevenueAggregatesQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId || Boolean(skip),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    data: data?.groupRevenueAggregates ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ---------- group schedule + lessons (lazy, for ScheduleTab) ---------- */

export type IGroupForLessons = NonNullable<GroupLessonsDoc['group']>;
export type IGroupLessonRow = NonNullable<
  NonNullable<IGroupForLessons['lessons']>[number]
>;

interface IGroupLessonsHook {
  group?: IGroupForLessons;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupLessons = (id?: string): IGroupLessonsHook => {
  const { data, loading, error, refetch } = useGroupLessonsQuery({
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    group: data?.group ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ---------- group-scoped Zoom recordings (TZ §G2) ---------- */

export type IGroupZoomRecording = NonNullable<
  NonNullable<
    NonNullable<AllZoomRecordingsDoc['allZoomRecordings']>['edges'][number]
  >['node']
>;

interface IGroupZoomRecordingsHook {
  recordings: IGroupZoomRecording[];
  hasNextPage: boolean;
  endCursor?: string | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

interface IGroupZoomRecordingsOpts {
  groupId?: string;
  dateFrom?: string;
  dateTo?: string;
  orphanOnly?: boolean;
  first?: number;
  /** Не запрашивать вовсе — роли без доступа к записям Zoom (финансист). */
  skip?: boolean;
}

export const useGroupZoomRecordings = (
  opts: IGroupZoomRecordingsOpts,
): IGroupZoomRecordingsHook => {
  const { data, loading, error, refetch } = useAllZoomRecordingsQuery({
    variables: {
      groupId: opts.groupId,
      dateFrom: opts.dateFrom,
      dateTo: opts.dateTo,
      orphanOnly: opts.orphanOnly,
      first: opts.first ?? 50,
    },
    skip: !opts.groupId || Boolean(opts.skip),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const edges = data?.allZoomRecordings?.edges || [];
  return {
    recordings: edges
      .map((e) => e?.node)
      .filter((n): n is IGroupZoomRecording => Boolean(n)),
    hasNextPage: Boolean(data?.allZoomRecordings?.pageInfo?.hasNextPage),
    endCursor: data?.allZoomRecordings?.pageInfo?.endCursor,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ---------- group materials (TZ §G6) ---------- */

export type IGroupMaterial = NonNullable<
  GroupMaterialsDoc['materials'][number]
>;

interface IGroupMaterialsHook {
  materials: IGroupMaterial[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

interface IGroupMaterialsOpts {
  groupId?: string;
  lessonId?: string;
  kind?: MaterialKindEnum;
}

export const useGroupMaterials = (
  opts: IGroupMaterialsOpts,
): IGroupMaterialsHook => {
  const { data, loading, error, refetch } = useGroupMaterialsQuery({
    variables: {
      groupId: opts.groupId || '',
      lessonId: opts.lessonId,
      kind: opts.kind,
    },
    skip: !opts.groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    materials: (data?.materials || []).filter((m): m is IGroupMaterial =>
      Boolean(m),
    ),
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};
